import React, { useState, useEffect, useCallback, useRef } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { s_TI } from '../../shared/styles';
import { getAgenda, saveAgenda, importIcal, getAbsences, getMembers } from './businessApi';
import TechLoadingBar from '../../components/ui/TechLoadingBar';

const DAYS_NL = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag', 'Zondag'];
const DAYS_EN = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const EVENT_TYPES = [
  { id: 'meeting',      icon: '📅', nl: 'Vergadering',  en: 'Meeting',      color: '#5B9ED0' },
  { id: 'training',     icon: '🎓', nl: 'Training',     en: 'Training',     color: '#7B6FD0' },
  { id: 'wellness',     icon: '💚', nl: 'Welzijn',      en: 'Wellness',     color: '#2A9E82' },
  { id: 'deadline',     icon: '⚠️', nl: 'Deadline',     en: 'Deadline',     color: '#C05050' },
  { id: 'announcement', icon: '📢', nl: 'Aankondiging', en: 'Announcement', color: '#C8A84B' },
  { id: 'other',        icon: '⚡', nl: 'Overig',       en: 'Other',        color: '#D4873A' },
];

function typeConfig(typeId) {
  return EVENT_TYPES.find(t => t.id === typeId) || EVENT_TYPES[EVENT_TYPES.length - 1];
}

function getMondayKey(offset = 0) {
  const d   = new Date();
  const dow = d.getDay() || 7;
  d.setDate(d.getDate() - (dow - 1) + offset * 7);
  return d.toISOString().slice(0, 10);
}

function formatFullDate(mondayStr, dayOffset, nl) {
  const d = new Date(mondayStr);
  d.setDate(d.getDate() + dayOffset);
  return d.toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

function isToday(mondayStr, dayOffset) {
  const d = new Date(mondayStr);
  d.setDate(d.getDate() + dayOffset);
  const today = new Date();
  return d.toDateString() === today.toDateString();
}

function dateForDay(mondayStr, dayOffset) {
  const d = new Date(mondayStr);
  d.setDate(d.getDate() + dayOffset);
  return d.toISOString().slice(0, 10);
}

const EMPTY_FORM = { title: '', type: 'meeting', start: '', end: '', description: '', meeting_link: '', attendees: [], recurring: false, rsvp: {} };

export default function BusinessAgendaScreen({ lang, businessId, currentUserId, role, isOwner }) {
  const nl           = lang === 'nl';
  const canManage    = isOwner || role === 'admin' || role === 'manager';
  const [weekOffset, setWeekOffset] = useState(0);
  const [agenda, setAgenda]         = useState({});
  const [absences, setAbsences]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [editItem, setEditItem]     = useState(null); // { dayIndex, itemIndex }
  const [newItem, setNewItem]       = useState(null); // { dayIndex }
  const [form, setForm]             = useState(EMPTY_FORM);
  const [filter, setFilter]         = useState('all');
  const [showIcal, setShowIcal]     = useState(false);
  const [icalUrl, setIcalUrl]       = useState('');
  const [icalLoading, setIcalLoading] = useState(false);
  const [icalMsg, setIcalMsg]       = useState(null);
  const [members, setMembers]       = useState([]);
  const [showExport, setShowExport] = useState(false);
  const [showGCal, setShowGCal]     = useState(false);
  const [copied, setCopied]         = useState(false);

  const weekKey = getMondayKey(weekOffset);

  const load = useCallback(() => {
    setLoading(true);
    const from = weekKey;
    const d2   = new Date(weekKey); d2.setDate(d2.getDate() + 6);
    const to   = d2.toISOString().slice(0, 10);
    Promise.all([
      getAgenda(businessId, weekKey),
      getAbsences(businessId, from, to).catch(() => []),
    ]).then(([agendaData, absData]) => {
      setAgenda(agendaData.agenda || {});
      setAbsences(Array.isArray(absData) ? absData : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [businessId, weekKey]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getMembers(businessId).then(d => { if (Array.isArray(d)) setMembers(d); }).catch(() => {});
  }, [businessId]); // eslint-disable-line

  const saveNext = async (nextAgenda) => {
    setSaving(true);
    await saveAgenda(businessId, { week_start: weekKey, agenda: nextAgenda });
    setSaving(false);
  };

  const canEditItem = (item) => canManage || item?.creatorId === currentUserId;

  const addItem = () => {
    if (!form.title.trim() || newItem === null) return;
    const tc   = typeConfig(form.type);
    const item = {
      title: form.title.trim(),
      type: form.type,
      start: form.start,
      end: form.end,
      color: tc.color,
      description: form.description.trim(),
      meeting_link: form.meeting_link.trim(),
      attendees: form.attendees || [],
      recurring: form.recurring,
      creatorId: currentUserId,
      rsvp: {},
    };
    const d    = newItem.dayIndex;
    const items = [...(agenda[d] || []), item];
    const next  = { ...agenda, [d]: items };
    setAgenda(next);
    saveNext(next);
    setNewItem(null);
    setForm(EMPTY_FORM);
  };

  const updateItem = () => {
    if (!editItem) return;
    const { dayIndex, itemIndex } = editItem;
    const tc = typeConfig(form.type);
    const items = (agenda[dayIndex] || []).map((it, i) =>
      i === itemIndex ? { ...it, ...form, color: tc.color } : it
    );
    const next = { ...agenda, [dayIndex]: items };
    setAgenda(next);
    saveNext(next);
    setEditItem(null);
  };

  const deleteItem = (dayIndex, itemIndex) => {
    const items = (agenda[dayIndex] || []).filter((_, i) => i !== itemIndex);
    const next  = { ...agenda, [dayIndex]: items };
    setAgenda(next);
    saveNext(next);
    setEditItem(null);
  };

  const rsvp = (dayIndex, itemIndex, status) => {
    const items = (agenda[dayIndex] || []).map((it, i) => {
      if (i !== itemIndex) return it;
      const cur = it.rsvp || {};
      if (cur[currentUserId] === status) {
        const { [currentUserId]: _, ...rest } = cur;
        return { ...it, rsvp: rest };
      }
      return { ...it, rsvp: { ...cur, [currentUserId]: status } };
    });
    const next = { ...agenda, [dayIndex]: items };
    setAgenda(next);
    saveNext(next);
  };

  const handleGoogleImport = (googleEvents) => {
    const next = { ...agenda };
    googleEvents.forEach(({ dayIndex, ...item }) => {
      next[dayIndex] = [...(next[dayIndex] || []), item];
    });
    setAgenda(next);
    saveNext(next);
  };

  const handleIcalImport = async () => {
    if (!icalUrl.trim()) return;
    setIcalLoading(true); setIcalMsg(null);
    const d = await importIcal(businessId, { url: icalUrl.trim(), week_start: weekKey });
    if (d.ok) {
      setIcalMsg(`✓ ${d.imported_count} ${nl ? 'items geïmporteerd' : 'items imported'}`);
      setAgenda(d.agenda || {});
    } else {
      setIcalMsg(`⚠ ${d.error || (nl ? 'Import mislukt' : 'Import failed')}`);
    }
    setIcalLoading(false);
  };

  // ── Export helpers ─────────────────────────────────────────────────────────
  const buildIcs = () => {
    const lines = [
      'BEGIN:VCALENDAR', 'VERSION:2.0',
      'PRODID:-//SensLifting//Business Agenda//EN',
      'CALSCALE:GREGORIAN', 'METHOD:PUBLISH',
    ];
    Object.entries(agenda).forEach(([di, items]) => {
      if (!Array.isArray(items)) return;
      const base = new Date(weekKey);
      base.setDate(base.getDate() + parseInt(di));
      const dateStr = base.toISOString().slice(0, 10).replace(/-/g, '');
      items.forEach((item, idx) => {
        const st = item.start ? item.start.replace(':', '') + '00' : '090000';
        const et = item.end   ? item.end.replace(':', '')   + '00' : '100000';
        lines.push('BEGIN:VEVENT');
        lines.push(`DTSTART:${dateStr}T${st}`);
        lines.push(`DTEND:${dateStr}T${et}`);
        lines.push(`SUMMARY:${(item.title || '').replace(/[,;]/g, ' ')}`);
        if (item.description) lines.push(`DESCRIPTION:${item.description.replace(/\n/g, '\\n')}`);
        if (item.meeting_link) lines.push(`URL:${item.meeting_link}`);
        lines.push(`UID:sl-${businessId}-${dateStr}-${idx}@senselifting.app`);
        lines.push('END:VEVENT');
      });
    });
    lines.push('END:VCALENDAR');
    return lines.join('\r\n');
  };

  const buildCsv = () => {
    const rows = [['Date', 'Day', 'Type', 'Title', 'Start', 'End', 'Description', 'Meeting Link']];
    const DAYS_LABEL = nl ? DAYS_NL : DAYS_EN;
    Object.entries(agenda).forEach(([di, items]) => {
      if (!Array.isArray(items)) return;
      const base = new Date(weekKey);
      base.setDate(base.getDate() + parseInt(di));
      const dateStr = base.toISOString().slice(0, 10);
      items.forEach(item => {
        rows.push([
          dateStr, DAYS_LABEL[parseInt(di)] || '', item.type || '', item.title || '',
          item.start || '', item.end || '',
          (item.description || '').replace(/"/g, '""'),
          item.meeting_link || '',
        ]);
      });
    });
    return rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
  };

  const downloadFile = (content, filename, mime) => {
    const blob = new Blob([content], { type: mime });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const webcalUrl = (() => {
    const host = window.location.hostname;
    return `webcal://${host}/api/businesses/${businessId}/agenda.ics`;
  })();

  const copyWebcal = () => {
    navigator.clipboard.writeText(webcalUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  const openNew = (dayIndex) => {
    setNewItem({ dayIndex }); setEditItem(null); setForm(EMPTY_FORM);
  };
  const openEdit = (dayIndex, itemIndex) => {
    const it = agenda[dayIndex]?.[itemIndex];
    if (!it || !canEditItem(it)) return;
    setEditItem({ dayIndex, itemIndex });
    setNewItem(null);
    setForm({ title: it.title || '', type: it.type || 'meeting', start: it.start || '', end: it.end || '', description: it.description || '', meeting_link: it.meeting_link || '', attendees: it.attendees || [], recurring: it.recurring || false, rsvp: it.rsvp || {} });
  };

  if (loading) return <TechLoadingBar lang={lang} />;

  const DAYS = nl ? DAYS_NL : DAYS_EN;

  // Filter helper
  const visibleItems = (items) => filter === 'all' ? items : items.filter(it => it.type === filter);

  return (
    <div style={{ padding: '0 0 32px' }}>
      <style>{CSS}</style>

      {/* Week nav */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <button onClick={() => setWeekOffset(w => w - 1)}
          style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.silver, padding: '8px 14px', cursor: 'pointer', fontSize: 16 }}>‹</button>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: C.white, fontSize: 13, fontWeight: 700 }}>
            {weekOffset === 0 ? (nl ? 'Deze week' : 'This week') : weekOffset === 1 ? (nl ? 'Volgende week' : 'Next week') : weekOffset === -1 ? (nl ? 'Vorige week' : 'Last week') : weekKey}
          </div>
          {saving && <div style={{ color: C.silver, fontSize: 10, marginTop: 2 }}>💾</div>}
          {weekOffset !== 0 && (
            <button onClick={() => setWeekOffset(0)}
              style={{ background: 'none', border: 'none', color: _T.accent, fontSize: 11, cursor: 'pointer', padding: '2px 0' }}>
              {nl ? 'Naar vandaag' : 'Go to today'}
            </button>
          )}
        </div>
        <button onClick={() => setWeekOffset(w => w + 1)}
          style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.silver, padding: '8px 14px', cursor: 'pointer', fontSize: 16 }}>›</button>
      </div>

      {/* Event type filter */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, marginBottom: 12 }}>
        <button onClick={() => setFilter('all')}
          style={{ flexShrink: 0, background: filter === 'all' ? `${_T.accent}20` : _T.bg4, border: `1.5px solid ${filter === 'all' ? _T.accent : _T.cardBorderBot}`, borderRadius: 20, color: filter === 'all' ? _T.accent : C.silver, fontSize: 11, fontWeight: 600, padding: '5px 12px', cursor: 'pointer' }}>
          {nl ? 'Alles' : 'All'}
        </button>
        {EVENT_TYPES.map(t => (
          <button key={t.id} onClick={() => setFilter(f => f === t.id ? 'all' : t.id)}
            style={{ flexShrink: 0, background: filter === t.id ? `${t.color}25` : _T.bg4, border: `1.5px solid ${filter === t.id ? t.color : _T.cardBorderBot}`, borderRadius: 20, color: filter === t.id ? t.color : C.silver, fontSize: 11, fontWeight: 600, padding: '5px 12px', cursor: 'pointer' }}>
            {t.icon} {nl ? t.nl : t.en}
          </button>
        ))}
      </div>

      {/* Import / Google Cal / Export buttons */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        {canManage && (
          <button onClick={() => { setShowIcal(v => !v); setShowExport(false); setShowGCal(false); }}
            style={{ flex: 1, background: showIcal ? `${_T.accent}18` : _T.bg4, border: `1px solid ${showIcal ? _T.accent : _T.cardBorderBot}`, borderRadius: 12, color: showIcal ? _T.accent : C.silver, fontSize: 11, padding: '9px 4px', cursor: 'pointer' }}>
            📅 {nl ? 'iCal' : 'iCal'}
          </button>
        )}
        <button onClick={() => { setShowGCal(v => !v); setShowIcal(false); setShowExport(false); }}
          style={{ flex: 1, background: showGCal ? '#EA433520' : _T.bg4, border: `1px solid ${showGCal ? '#EA4335' : _T.cardBorderBot}`, borderRadius: 12, color: showGCal ? '#EA4335' : C.silver, fontSize: 11, padding: '9px 4px', cursor: 'pointer' }}>
          🗓 Google
        </button>
        <button onClick={() => { setShowExport(v => !v); setShowIcal(false); setShowGCal(false); }}
          style={{ flex: 1, background: showExport ? `${_T.accent}18` : _T.bg4, border: `1px solid ${showExport ? _T.accent : _T.cardBorderBot}`, borderRadius: 12, color: showExport ? _T.accent : C.silver, fontSize: 11, padding: '9px 4px', cursor: 'pointer' }}>
          ⬇ {nl ? 'Export' : 'Export'}
        </button>
      </div>

      {showIcal && (
        <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: 14, marginBottom: 14 }}>
          <div style={{ color: C.silver, fontSize: 11, marginBottom: 8 }}>{nl ? 'iCal / Outlook kalender-URL' : 'iCal / Outlook calendar URL'}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={icalUrl} onChange={e => setIcalUrl(e.target.value)} placeholder="https://outlook.office365.com/..."
              style={{ ...s_TI({ flex: 1 }), fontSize: 12 }} />
            <button onClick={handleIcalImport} disabled={icalLoading}
              style={{ background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 10, color: '#0a1220', fontSize: 12, fontWeight: 700, padding: '0 14px', cursor: 'pointer', flexShrink: 0 }}>
              {icalLoading ? '...' : (nl ? 'Importeer' : 'Import')}
            </button>
          </div>
          {icalMsg && <div style={{ color: icalMsg.startsWith('✓') ? C.teal : C.red, fontSize: 11, marginTop: 6 }}>{icalMsg}</div>}
        </div>
      )}

      {showGCal && (
        <GoogleCalendarImport
          nl={nl}
          weekKey={weekKey}
          onImported={(evs) => { handleGoogleImport(evs); setShowGCal(false); }}
        />
      )}

      {/* Export panel */}
      {showExport && (
        <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: 14, marginBottom: 14 }}>
          <div style={{ color: C.white, fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
            {nl ? 'Agenda exporteren' : 'Export Agenda'}
          </div>

          {/* ICS download */}
          <button onClick={() => downloadFile(buildIcs(), `agenda-${weekKey}.ics`, 'text/calendar')}
            style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, padding: '11px 14px', cursor: 'pointer', marginBottom: 8, textAlign: 'left' }}>
            <span style={{ fontSize: 20 }}>📆</span>
            <div>
              <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{nl ? 'Downloaden als .ics' : 'Download as .ics'}</div>
              <div style={{ color: C.silver, fontSize: 11 }}>{nl ? 'Openen in Apple Calendar, Outlook of Google Calendar' : 'Open in Apple Calendar, Outlook or Google Calendar'}</div>
            </div>
          </button>

          {/* CSV download */}
          <button onClick={() => downloadFile(buildCsv(), `agenda-${weekKey}.csv`, 'text/csv')}
            style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, padding: '11px 14px', cursor: 'pointer', marginBottom: 8, textAlign: 'left' }}>
            <span style={{ fontSize: 20 }}>📊</span>
            <div>
              <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{nl ? 'Downloaden als CSV' : 'Download as CSV'}</div>
              <div style={{ color: C.silver, fontSize: 11 }}>{nl ? 'Openen in Excel of Google Sheets' : 'Open in Excel or Google Sheets'}</div>
            </div>
          </button>

          {/* Webcal subscribe */}
          <div style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, padding: '11px 14px', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 18 }}>🔔</span>
              <div>
                <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{nl ? 'Webcal-abonnement' : 'Webcal Subscription'}</div>
                <div style={{ color: C.silver, fontSize: 11 }}>{nl ? 'Automatisch synchroniseren met je agenda-app' : 'Auto-sync with your calendar app'}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ flex: 1, background: '#080e1a', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 8, padding: '7px 10px', fontSize: 10, color: C.silver2, wordBreak: 'break-all', lineHeight: 1.4 }}>
                {webcalUrl}
              </div>
              <button onClick={copyWebcal}
                style={{ background: copied ? '#4AC87A20' : `${_T.accent}20`, border: `1px solid ${copied ? '#4AC87A55' : _T.accent + '55'}`, borderRadius: 8, color: copied ? '#4AC87A' : _T.accent, fontSize: 11, fontWeight: 700, padding: '7px 12px', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
                {copied ? '✓' : (nl ? 'Kopieer' : 'Copy')}
              </button>
            </div>
          </div>

          <div style={{ color: C.silver2, fontSize: 10, lineHeight: 1.5 }}>
            💡 {nl ? 'Tip: plak de webcal-URL in Apple Calendar → Bestand → Nieuw kalenderabonnement' : 'Tip: paste webcal URL in Apple Calendar → File → New Calendar Subscription'}
          </div>
        </div>
      )}

      {/* Day columns */}
      {DAYS.map((dayLabel, di) => {
        const rawItems   = agenda[di] || [];
        const items      = visibleItems(rawItems);
        const dateStr    = formatFullDate(weekKey, di, nl);
        const todayMark  = isToday(weekKey, di);
        const dayDate    = dateForDay(weekKey, di);
        const dayAbs     = absences.filter(a => a.absence_date === dayDate);

        return (
          <div key={di} style={{ background: _T.bg3, border: `1.5px solid ${todayMark ? _T.accent : _T.cardBorderBot}`, borderRadius: 14, marginBottom: 10, overflow: 'hidden' }}>
            {/* Day header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: todayMark ? `${_T.accent}10` : _T.bg4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div>
                  <span style={{ color: todayMark ? _T.accent : C.white, fontSize: 13, fontWeight: 700 }}>{dayLabel}</span>
                  <span style={{ color: C.silver, fontSize: 11, marginLeft: 8 }}>{dateStr}</span>
                </div>
                {todayMark && <span style={{ background: _T.accent, color: '#0a1220', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 6 }}>{nl ? 'VANDAAG' : 'TODAY'}</span>}
              </div>
              {/* Anyone can add events */}
              <button onClick={() => openNew(di)}
                style={{ background: `${_T.accent}20`, border: `1px solid ${_T.accent}44`, borderRadius: 8, color: _T.accent, fontSize: 16, width: 28, height: 28, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>+</button>
            </div>

            {/* Absence indicators */}
            {dayAbs.length > 0 && (
              <div style={{ padding: '6px 14px', background: `#C0505010`, borderBottom: `1px solid #C0505030` }}>
                {dayAbs.map((a, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: i < dayAbs.length - 1 ? 3 : 0 }}>
                    <div style={{ width: 3, height: 14, borderRadius: 2, background: '#C05050', flexShrink: 0 }} />
                    <span style={{ color: '#C05050', fontSize: 11 }}>
                      🤒 {a.member_name || (nl ? 'Medewerker' : 'Member')}
                      {a.reason ? ` — ${a.reason}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Events */}
            {items.map((it, ii) => {
              const tc       = typeConfig(it.type);
              const myRsvp   = it.rsvp?.[currentUserId];
              const yesCount = Object.values(it.rsvp || {}).filter(v => v === 'yes').length;
              const editable = canEditItem(it);
              return (
                <div key={ii} style={{ borderBottom: ii < items.length - 1 ? `1px solid ${_T.cardBorderBot}` : 'none' }}>
                  <div onClick={() => editable && openEdit(di, ii)}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '11px 14px', cursor: editable ? 'pointer' : 'default' }}>
                    <div style={{ width: 3, minHeight: 36, borderRadius: 2, background: tc.color, flexShrink: 0, marginTop: 2 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <span style={{ fontSize: 12 }}>{tc.icon}</span>
                        <span style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{it.title}</span>
                        {it.recurring && <span style={{ color: C.silver, fontSize: 10 }}>🔄</span>}
                        {editable && <span style={{ color: C.silver2, fontSize: 10, marginLeft: 'auto' }}>✏️</span>}
                      </div>
                      {(it.start || it.end) && (
                        <div style={{ color: C.silver, fontSize: 11 }}>{it.start}{it.end ? ` – ${it.end}` : ''}</div>
                      )}
                      {it.description && (
                        <div style={{ color: C.silver2, fontSize: 11, marginTop: 2 }}>{it.description}</div>
                      )}
                      {it.meeting_link && (
                        <a href={it.meeting_link} target="_blank" rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: _T.accent, fontSize: 11, marginTop: 3, textDecoration: 'none', fontWeight: 600 }}>
                          🔗 {nl ? 'Deelnemen aan vergadering' : 'Join meeting'}
                        </a>
                      )}
                      {it.attendees && it.attendees.length > 0 && (
                        <div style={{ color: C.silver2, fontSize: 10, marginTop: 2 }}>
                          👥 {it.attendees.length} {nl ? 'uitgenodigd' : 'invited'}
                        </div>
                      )}
                      {/* RSVP bar */}
                      <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        {[['yes', '✓', '#4AC87A'], ['no', '✗', '#C05050'], ['maybe', '?', '#C8A84B']].map(([s, label, color]) => (
                          <button key={s} onClick={e => { e.stopPropagation(); rsvp(di, ii, s); }}
                            style={{ padding: '3px 9px', borderRadius: 6, border: `1.5px solid ${myRsvp === s ? color : _T.cardBorderBot}`, background: myRsvp === s ? `${color}22` : _T.bg4, color: myRsvp === s ? color : C.silver2, fontSize: 11, fontWeight: myRsvp === s ? 700 : 400, cursor: 'pointer' }}>
                            {label}
                          </button>
                        ))}
                        {yesCount > 0 && (
                          <span style={{ color: '#4AC87A', fontSize: 11, marginLeft: 4 }}>
                            {yesCount} {nl ? 'aanwezig' : 'attending'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* No events placeholder */}
            {rawItems.length === 0 && newItem?.dayIndex !== di && (
              <div style={{ padding: '10px 14px', color: C.silver2, fontSize: 11 }}>
                {nl ? 'Geen items — klik + om toe te voegen' : 'No items — tap + to add'}
              </div>
            )}

            {/* New item form */}
            {newItem?.dayIndex === di && (
              <AgendaForm form={form} setForm={setForm} onSave={addItem} onCancel={() => setNewItem(null)} nl={nl} members={members} />
            )}
            {/* Edit item form */}
            {editItem?.dayIndex === di && (
              <AgendaForm form={form} setForm={setForm} onSave={updateItem} onCancel={() => setEditItem(null)}
                onDelete={canManage ? () => deleteItem(editItem.dayIndex, editItem.itemIndex) : null} nl={nl} isEdit members={members} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Google Calendar import ─────────────────────────────────────────────────────
const GCAL_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GCAL_SCOPE     = 'https://www.googleapis.com/auth/calendar.readonly';

function mapGoogleEvent(ev, weekKey) {
  const startDt = ev.start?.dateTime ? new Date(ev.start.dateTime) : null;
  const endDt   = ev.end?.dateTime   ? new Date(ev.end.dateTime)   : null;

  // Day index relative to Monday of week (0–6)
  let dayIndex = 0;
  if (startDt) {
    const monday = new Date(weekKey);
    dayIndex = Math.max(0, Math.min(6, Math.round((startDt - monday) / 86400000)));
  }

  const fmt = (d) => d ? `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}` : '';

  const title = (ev.summary || '').toLowerCase();
  let type = 'meeting';
  if (/training|workshop|course|cursus|opleiding/.test(title)) type = 'training';
  else if (/wellness|yoga|sport|gym|fitness|mindful|meditati/.test(title)) type = 'wellness';
  else if (/deadline|due|inleveren/.test(title)) type = 'deadline';
  else if (/aankondiging|announcement|nieuw|update|info/.test(title)) type = 'announcement';

  const meetingLink =
    ev.hangoutLink ||
    ev.conferenceData?.entryPoints?.find(e => e.entryPointType === 'video')?.uri ||
    '';

  return {
    dayIndex,
    title:        ev.summary || '(No title)',
    type,
    start:        fmt(startDt),
    end:          fmt(endDt),
    description:  ev.description ? ev.description.replace(/<[^>]+>/g, '') : '',
    meeting_link: meetingLink,
    color:        typeConfig(type).color,
    recurring:    !!(ev.recurrence || ev.recurringEventId),
    attendees:    [],
    rsvp:         {},
  };
}

function GoogleCalendarImport({ nl, weekKey, onImported }) {
  const [status, setStatus]   = useState('idle'); // idle | loading | preview | error
  const [events, setEvents]   = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [error, setError]     = useState(null);
  const tokenRef = useRef(null);

  // Load Google GSI script once
  useEffect(() => {
    if (document.getElementById('gsi-script')) return;
    const s = document.createElement('script');
    s.id  = 'gsi-script';
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    document.head.appendChild(s);
  }, []);

  const fetchEvents = async (token) => {
    const monday = new Date(weekKey);
    const sunday = new Date(weekKey);
    sunday.setDate(sunday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);

    const params = new URLSearchParams({
      timeMin:       monday.toISOString(),
      timeMax:       sunday.toISOString(),
      singleEvents:  'true',
      orderBy:       'startTime',
      maxResults:    '50',
    });

    try {
      const res  = await fetch(
        `https://www.googleapis.com/calendar/v3/calendars/primary/events?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.error) { setError(data.error.message); setStatus('error'); return; }

      const mapped = (data.items || [])
        .filter(ev => ev.start?.dateTime) // skip all-day events without time
        .map(ev => mapGoogleEvent(ev, weekKey));

      setEvents(mapped);
      setSelected(new Set(mapped.map((_, i) => i)));
      setStatus('preview');
    } catch (e) {
      setError(e.message);
      setStatus('error');
    }
  };

  const connect = () => {
    setError(null);
    const tryConnect = () => {
      if (!window.google?.accounts?.oauth2) {
        setError(nl ? 'Google nog niet geladen. Probeer opnieuw.' : 'Google not loaded yet. Please try again.');
        setStatus('error');
        return;
      }
      setStatus('loading');
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: GCAL_CLIENT_ID,
        scope:     GCAL_SCOPE,
        callback:  (resp) => {
          if (resp.error) {
            setError(resp.error_description || resp.error);
            setStatus('error');
            return;
          }
          tokenRef.current = resp.access_token;
          fetchEvents(resp.access_token);
        },
      });
      client.requestAccessToken({ prompt: 'select_account' });
    };

    // If script not yet loaded, wait briefly
    if (!window.google?.accounts?.oauth2) {
      setTimeout(tryConnect, 1200);
    } else {
      tryConnect();
    }
  };

  const toggleSelect = (i) => {
    setSelected(s => {
      const n = new Set(s);
      n.has(i) ? n.delete(i) : n.add(i);
      return n;
    });
  };

  const doImport = () => {
    const toImport = events.filter((_, i) => selected.has(i));
    onImported(toImport);
  };

  const DAYS_LABEL = nl
    ? ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo']
    : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div style={{ background: _T.bg3, border: `1px solid #EA433533`, borderRadius: 14, padding: 14, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        {/* Google colour dots */}
        <div style={{ display: 'flex', gap: 3 }}>
          {['#EA4335','#FBBC04','#34A853','#4285F4'].map(c => (
            <div key={c} style={{ width: 6, height: 6, borderRadius: '50%', background: c }} />
          ))}
        </div>
        <div style={{ color: 'white', fontSize: 13, fontWeight: 700 }}>Google Calendar</div>
      </div>

      {status === 'idle' && (
        <>
          <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.6, marginBottom: 14 }}>
            {nl
              ? 'Verbind je Google Calendar om afspraken voor deze week te importeren.'
              : 'Connect your Google Calendar to import appointments for this week.'}
          </div>
          <button onClick={connect}
            style={{ width: '100%', background: 'white', border: 'none', borderRadius: 10, color: '#3c4043', fontSize: 13, fontWeight: 600, padding: '11px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {nl ? 'Verbinden met Google' : 'Connect with Google'}
          </button>
        </>
      )}

      {status === 'loading' && (
        <div style={{ textAlign: 'center', padding: '20px 0', color: C.silver, fontSize: 13 }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>
          {nl ? 'Verbinden met Google Calendar...' : 'Connecting to Google Calendar...'}
        </div>
      )}

      {status === 'error' && (
        <div>
          <div style={{ color: '#EA4335', fontSize: 12, marginBottom: 12, lineHeight: 1.5 }}>
            ⚠ {error || (nl ? 'Er ging iets mis.' : 'Something went wrong.')}
          </div>
          <button onClick={connect}
            style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.silver, fontSize: 12, padding: '8px 16px', cursor: 'pointer' }}>
            {nl ? 'Opnieuw proberen' : 'Try again'}
          </button>
        </div>
      )}

      {status === 'preview' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ color: C.silver, fontSize: 12 }}>
              {events.length === 0
                ? (nl ? 'Geen afspraken gevonden deze week.' : 'No appointments found this week.')
                : (nl ? `${events.length} afspraken gevonden` : `${events.length} appointments found`)}
            </div>
            {events.length > 0 && (
              <button
                onClick={() => setSelected(selected.size === events.length ? new Set() : new Set(events.map((_, i) => i)))}
                style={{ background: 'none', border: 'none', color: _T.accent, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
                {selected.size === events.length ? (nl ? 'Geen' : 'None') : (nl ? 'Alles' : 'All')}
              </button>
            )}
          </div>

          {events.length > 0 && (
            <>
              <div style={{ maxHeight: 280, overflowY: 'auto', marginBottom: 12 }}>
                {events.map((ev, i) => {
                  const tc  = typeConfig(ev.type);
                  const sel = selected.has(i);
                  return (
                    <button key={i} onClick={() => toggleSelect(i)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', background: sel ? `${tc.color}12` : _T.bg4, border: `1.5px solid ${sel ? tc.color + '55' : _T.cardBorderBot}`, borderRadius: 10, padding: '9px 12px', marginBottom: 6, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}>
                      <div style={{ width: 18, height: 18, borderRadius: 5, border: `2px solid ${sel ? tc.color : _T.cardBorderBot}`, background: sel ? tc.color : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {sel && <span style={{ color: 'white', fontSize: 10, fontWeight: 700 }}>✓</span>}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 10 }}>{tc.icon}</span>
                          <span style={{ color: 'white', fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.title}</span>
                        </div>
                        <div style={{ color: C.silver2, fontSize: 10, marginTop: 2 }}>
                          {DAYS_LABEL[ev.dayIndex]}  {ev.start}{ev.end ? ` – ${ev.end}` : ''}
                          {ev.recurring && ' 🔄'}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <button onClick={doImport} disabled={selected.size === 0}
                style={{ width: '100%', background: selected.size > 0 ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : _T.bg4, border: 'none', borderRadius: 10, color: selected.size > 0 ? '#0a1220' : C.silver2, fontSize: 13, fontWeight: 700, padding: '11px', cursor: selected.size > 0 ? 'pointer' : 'not-allowed' }}>
                {nl ? `${selected.size} afspraken importeren` : `Import ${selected.size} appointment${selected.size !== 1 ? 's' : ''}`}
              </button>
            </>
          )}

          {events.length === 0 && (
            <button onClick={connect}
              style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.silver, fontSize: 12, padding: '8px 16px', cursor: 'pointer', marginTop: 6 }}>
              {nl ? 'Andere week — opnieuw ophalen' : 'Different week — fetch again'}
            </button>
          )}
        </>
      )}
    </div>
  );
}

function AgendaForm({ form, setForm, onSave, onCancel, onDelete, nl, isEdit, members }) {
  const tc = typeConfig(form.type);
  const hasMeetingLink = ['meeting', 'training', 'wellness'].includes(form.type);

  const toggleAttendee = (uid) => {
    setForm(f => {
      const cur = f.attendees || [];
      return { ...f, attendees: cur.includes(uid) ? cur.filter(x => x !== uid) : [...cur, uid] };
    });
  };

  return (
    <div style={{ padding: 14, background: `${_T.accent}06`, borderTop: `1px solid ${_T.cardBorderBot}` }}>
      {/* Title */}
      <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
        placeholder={nl ? 'Titel *' : 'Title *'}
        style={{ ...s_TI({ width: '100%', boxSizing: 'border-box', marginBottom: 8 }), fontSize: 13 }} />

      {/* Type selector */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {EVENT_TYPES.map(t => (
          <button key={t.id} onClick={() => setForm(f => ({ ...f, type: t.id }))}
            style={{ padding: '5px 10px', borderRadius: 20, border: `1.5px solid ${form.type === t.id ? t.color : _T.cardBorderBot}`, background: form.type === t.id ? `${t.color}22` : _T.bg4, color: form.type === t.id ? t.color : C.silver, fontSize: 11, fontWeight: form.type === t.id ? 700 : 400, cursor: 'pointer' }}>
            {t.icon} {nl ? t.nl : t.en}
          </button>
        ))}
      </div>

      {/* Time */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input type="time" value={form.start} onChange={e => setForm(f => ({ ...f, start: e.target.value }))}
          style={{ ...s_TI({ flex: 1 }), fontSize: 13 }} />
        <input type="time" value={form.end} onChange={e => setForm(f => ({ ...f, end: e.target.value }))}
          style={{ ...s_TI({ flex: 1 }), fontSize: 13 }} />
      </div>

      {/* Description */}
      <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
        placeholder={nl ? 'Omschrijving (optioneel)' : 'Description (optional)'}
        style={{ ...s_TI({ width: '100%', boxSizing: 'border-box', marginBottom: 8 }), fontSize: 13 }} />

      {/* Meeting link (for meetings/training/wellness) */}
      {hasMeetingLink && (
        <input value={form.meeting_link} onChange={e => setForm(f => ({ ...f, meeting_link: e.target.value }))}
          placeholder="🔗 Zoom / Teams / Meet URL"
          style={{ ...s_TI({ width: '100%', boxSizing: 'border-box', marginBottom: 8 }), fontSize: 12 }} />
      )}

      {/* Attendees */}
      {members.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ color: C.silver, fontSize: 11, marginBottom: 6 }}>
            👥 {nl ? 'Uitnodigingen' : 'Invite attendees'}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {members.map(m => {
              const sel = (form.attendees || []).includes(m.user_id);
              return (
                <button key={m.user_id} onClick={() => toggleAttendee(m.user_id)}
                  style={{ padding: '4px 10px', borderRadius: 16, border: `1.5px solid ${sel ? _T.accent : _T.cardBorderBot}`, background: sel ? `${_T.accent}20` : _T.bg4, color: sel ? _T.accent : C.silver, fontSize: 11, fontWeight: sel ? 700 : 400, cursor: 'pointer' }}>
                  {m.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Recurring toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <button onClick={() => setForm(f => ({ ...f, recurring: !f.recurring }))}
          style={{ width: 36, height: 20, borderRadius: 10, background: form.recurring ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : _T.bg4, border: `1px solid ${form.recurring ? _T.accent : _T.cardBorderBot}`, cursor: 'pointer', position: 'relative', transition: 'all 0.2s', flexShrink: 0, padding: 0 }}>
          <div style={{ position: 'absolute', top: 2, left: form.recurring ? 16 : 2, width: 14, height: 14, borderRadius: '50%', background: form.recurring ? '#0a1220' : C.silver, transition: 'left 0.2s' }} />
        </button>
        <span style={{ color: C.silver, fontSize: 12 }}>
          🔄 {nl ? 'Wekelijks herhalend' : 'Recurring weekly'}
        </span>
      </div>

      {/* Color preview */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: tc.color }} />
        <span style={{ color: C.silver, fontSize: 11 }}>{tc.icon} {nl ? tc.nl : tc.en}</span>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onCancel}
          style={{ flex: 1, background: 'transparent', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.silver, fontSize: 13, padding: '9px', cursor: 'pointer' }}>
          {nl ? 'Annuleren' : 'Cancel'}
        </button>
        {isEdit && onDelete && (
          <button onClick={onDelete}
            style={{ background: 'transparent', border: `1px solid #C0505055`, borderRadius: 10, color: '#C05050', fontSize: 13, padding: '9px 14px', cursor: 'pointer' }}>🗑</button>
        )}
        <button onClick={onSave} disabled={!form.title.trim()}
          style={{ flex: 1, background: form.title.trim() ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : _T.bg4, border: 'none', borderRadius: 10, color: form.title.trim() ? '#0a1220' : C.silver2, fontSize: 13, fontWeight: 700, padding: '9px', cursor: form.title.trim() ? 'pointer' : 'not-allowed' }}>
          {isEdit ? (nl ? 'Opslaan' : 'Save') : (nl ? 'Toevoegen' : 'Add')}
        </button>
      </div>
    </div>
  );
}
